import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type Perfil = 'Qualidade' | 'Planejamento' | 'Medição' | 'Fiscalização' | string;

interface AuditedDecisionBadgeProps {
  /** Perfil de governança que registrou a decisão (linguagem de negócio) */
  perfil: Perfil;
  /** Data/hora da decisão (ISO ou yyyy-mm-dd). Opcional. */
  decidedAt?: string | null;
  /** Variante visual */
  tone?: 'aprovada' | 'rejeitada' | 'neutra';
  className?: string;
}

/**
 * Selo institucional que materializa uma decisão como "recibo de governança".
 * Puramente apresentacional: não dispara queries nem expõe identificadores técnicos.
 */
export function AuditedDecisionBadge({
  perfil,
  decidedAt,
  tone = 'neutra',
  className = '',
}: AuditedDecisionBadgeProps) {
  let dateLabel: string | null = null;
  if (decidedAt) {
    try {
      const d = parseISO(decidedAt.includes('T') ? decidedAt : decidedAt + 'T00:00:00');
      dateLabel = format(d, "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      dateLabel = null;
    }
  }

  const toneClass =
    tone === 'aprovada'
      ? 'border-emerald-200 text-emerald-700 bg-emerald-50/60 dark:border-emerald-900 dark:text-emerald-300 dark:bg-emerald-950/30'
      : tone === 'rejeitada'
        ? 'border-rose-200 text-rose-700 bg-rose-50/60 dark:border-rose-900 dark:text-rose-300 dark:bg-rose-950/30'
        : 'border-border text-muted-foreground bg-muted/40';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] leading-none font-medium cursor-default select-none ${toneClass} ${className}`}
        >
          <ShieldCheck className="h-3 w-3" strokeWidth={2} />
          <span>Decisão auditada</span>
          <span className="opacity-60">•</span>
          <span>{perfil}</span>
          {dateLabel && (
            <>
              <span className="opacity-60">•</span>
              <span>{dateLabel}</span>
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs">
        Decisão registrada com trilha de auditoria pelo perfil de {perfil}
        {dateLabel ? ` em ${dateLabel}.` : '.'} A trilha é preservada para fins de governança e conformidade.
      </TooltipContent>
    </Tooltip>
  );
}

export default AuditedDecisionBadge;
